<script lang="ts" setup>
  import type { RouteLocationRaw } from 'vue-router'

  import { computed } from 'vue'

  const props = withDefaults(
    defineProps<{
      caption?: string
      link?: RouteLocationRaw
      linkIsExternal?: boolean
      color?: string
      as?: string
      left?: boolean
      disabled?: boolean
      isToggle?: boolean
      btnClasses?: string
      type?: 'button' | 'reset' | 'submit'

      ariaLabel?: string
    }>(),
    {
      caption: '',
      link: '',
      linkIsExternal: false,
      color: 'black',
      left: true,
      as: undefined,
      disabled: false,
      isToggle: false,
      btnClasses: 'px-6',
      type: undefined,

      ariaLabel: '',
    }
  )

  const disabledClasses = computed(() => {
    if (props.isToggle) return 'toggle-disabled'

    const isTransparent = /transparent/.test(props.color)

    const classes = `${
      isTransparent ? 'transparent-disabled' : 'filled-disabled'
    } opacity-25 border-black`

    return props.disabled ? classes : ''
  })
</script>

<template>
  <div
    class="button flex h-10 items-center cursor-pointer rounded text-sm"
    :class="[props.color, disabledClasses]"
  >
    <template v-if="props.link">
      <router-link
        v-if="!props.linkIsExternal"
        :to="props.link"
        :type="props.as"
        :disabled="props.disabled"
        class="flex w-full h-full items-center justify-center text-center"
        :class="props.btnClasses"
        :aria-label="props.ariaLabel"
      >
        <!-- Icon -->
        <slot v-if="props.left" />

        <!-- Caption -->
        <span v-if="props.caption" :class="props.left ? '' : 'mr-2'">
          {{ props.caption }}
        </span>

        <!-- Icon -->
        <slot v-if="!props.left" />
      </router-link>

      <a
        v-else
        :href="props.disabled ? '#' : (props.link as string)"
        :type="props.as"
        class="flex w-full h-full items-center justify-center text-center"
        :class="props.btnClasses"
        :aria-label="props.ariaLabel"
      >
        <!-- Icon -->
        <slot v-if="props.left" />

        <!-- Caption -->
        <span v-if="props.caption" :class="props.left ? '' : 'mr-2'">
          {{ props.caption }}
        </span>

        <!-- Icon -->
        <slot v-if="!props.left" />
      </a>
    </template>

    <button
      v-else
      class="flex w-full h-full items-center justify-center text-center"
      :class="props.btnClasses"
      :disabled="props.disabled"
      :type="props.type"
      :aria-label="props.ariaLabel"
    >
      <!-- Icon -->
      <slot v-if="props.left" />

      <!-- Caption -->
      <span v-if="props.caption" :class="props.left ? '' : 'mr-2'">
        {{ props.caption }}
      </span>

      <!-- Icon -->
      <slot v-if="!props.left" />
    </button>
  </div>
</template>

<style scoped>
  :slotted(svg),
  :slotted(span) {
    display: inline-block;
    vertical-align: middle;
  }
  :slotted(.icon) {
    display: inline-block;
    vertical-align: middle;
    margin-right: 16px;
  }
  .button {
    border: 1px solid transparent;
    transition: all 0.1s ease-out;
  }
  /* Colors */
  .button.black {
    border-color: black;
    background: black;
    color: white;
  }
  .button.white {
    border-color: white;
    background: white;
    color: black;
  }
  .button.transparent-black {
    background: transparent;
    color: black;
  }
  .button.transparent-red {
    background: transparent;
    border-color: theme('colors.mred');
    color: theme('colors.mred');
  }
  .button.transparent-mgray2 {
    background: transparent;
    border-color: theme('colors.mgray2');
    color: theme('colors.mgray2');
  }
  .button.border-mgray2 {
    border-color: theme('colors.mgray2');
  }
  .button.transparent-disabled {
    color: black;
  }
  .button.filled-disabled {
    background: black;
    color: white;
  }
  .button.border-black {
    border-color: black;
  }
  .button.border-white {
    border-color: white;
  }
  .button.toggle-disabled {
    background: transparent;
    color: theme('colors.mgray');
    border-color: transparent;
  }
</style>
